<script setup lang="ts">
export interface FormInput {
  id: string;
  title: string;
  subtitle?: string;
  type: string;
  value: string;
  required: boolean;
  showTitle?: boolean;
  show?: boolean;
  error: string;
  width?: string;
  name?: string;
  login?: boolean;
  disabled?: boolean;
  uppercase?: boolean;
  options?: { name: string; value: string | number }[];
  searchable?: boolean;
  custom?: boolean;
  placeHolder?: string;
  index?: number;
  [key: string]: unknown;
}

interface Props {
  inputs: FormInput[];
  stacked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  stacked: true,
});

const emit = defineEmits<{
  returnForm: [fields: FormInput[]];
  enterPressed: [value: boolean];
  returnSearch: [term: string];
}>();

const fields = ref<FormInput[]>([...props.inputs]);
const touchedFields = ref<Set<number>>(new Set());

watch(
  () => props.inputs,
  (to) => {
    fields.value = [...to];
  },
  { deep: true },
);

onMounted(() => {
  // Emit initial field state without running validation
  emit("returnForm", fields.value);
});

function updateForm(field: FormInput) {
  // Mark field as touched when it comes from user interaction
  if (field.index !== undefined) {
    touchedFields.value.add(field.index);
  }

  let reg: RegExp | undefined;
  let error = "";

  // Check for URLs in any field
  if (
    field.value &&
    field.type !== "email" &&
    /https?:\/\/|www\.|\.com|\.org|\.net|\.maps|google/i.test(field.value)
  ) {
    error = "URLs are not allowed";
  }

  // General validation for required fields (only if touched or has value)
  if (
    field.required &&
    (!field.value || field.value.length === 0) &&
    touchedFields.value.has(field.index ?? -1)
  ) {
    error = `Please enter ${field.title.toLowerCase()}...`;
  }

  // Phone number validation
  if (field.type === "phone" && field.value?.length > 0) {
    if (field.error) {
      error = "Please enter a valid phone number that relates to the country code selected";
    } else {
      error = "";
    }
  }

  // Street validation
  if (
    field.title === "Building name, street name and number" &&
    field.value &&
    field.value.length > 0
  ) {
    field.value = field.value.trim();
  }

  // Postcode validation and formatting
  if (field.type === "postcode" && field.value?.length > 0) {
    const stripped = field.value.toUpperCase().replace(/\s/g, "");
    field.value = `${stripped.slice(0, stripped.length - 3)} ${stripped.slice(stripped.length - 3)}`;
    reg =
      /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/;
    error = !reg.test(field.value) ? "Please enter a valid UK postcode" : "";
  }

  // City validation
  if (field.title === "City" && field.value && field.value.length > 0) {
    reg = /^[a-zA-Z0-9\s\-,'.]+$/;
    if (!reg.test(field.value)) {
      error = "City can only contain letters, spaces, hyphens";
    }
  }

  // Password validation
  if (field.type === "password" && field.required && field.value?.length > 0 && !field.login) {
    reg =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!"£#$%&'()*+,-./\\:;?@[\]^_`{|}~\s])[A-Za-z\d!"£#$%&'()*+,-./\\:;?@[\]^_`{|}~\s]{8,}$/;
    error = !reg.test(field.value)
      ? "Password should be a minimum of eight characters, at least one number and one special character"
      : "";
  }

  // Email validation
  if (field.type === "email" && field.value && field.value.length > 0) {
    reg = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
    error = !reg.test(field.value) ? "Please enter a valid email address" : "";
  }

  // Update fields
  fields.value = fields.value.map((input, index) => ({
    ...input,
    value: field.index === index ? field.value : input.value,
    error: field.index === index ? error : input.error,
  }));

  emit("returnForm", fields.value);
}

// Child TextInputs, by field index, so a submit can flush their pending
// debounce before we validate. Not a ref — we only ever call methods on these.
const textInputs = new Map<number, { flush?: () => void }>();

function setTextInputRef(el: unknown, index: number) {
  if (el) textInputs.set(index, el as { flush?: () => void });
  else textInputs.delete(index);
}

/**
 * Pull every child TextInput's current value into `fields` immediately.
 *
 * TextInput debounces by 500 ms, so without this a submit within that window
 * validates and posts the previous contents of the form (ticket 1143). Each
 * flush emits `returnValue` synchronously, so `fields` — and anything the
 * parent computes from `returnForm` — is current by the time this returns.
 */
function flush() {
  textInputs.forEach((input) => input?.flush?.());
}

function enterPressed() {
  flush();
  // Mark all fields as touched and validate on submit
  fields.value.forEach((field, i) => {
    touchedFields.value.add(i);
    updateForm({ ...field, index: i });
  });
  emit("enterPressed", true);
}

defineExpose({ flush });

function updateSearchTerm(searchTerm: string) {
  emit("returnSearch", searchTerm);
}
</script>

<template>
  <div
    class="dynamic-form"
    :class="!stacked && 'field-rows'"
  >
    <div
      v-for="(field, i) in fields"
      :key="`field_${i}`"
      class="dynamic-form__wrapper"
      :class="!stacked && 'field-rows'"
    >
      <CheckboxList
        v-if="field.type === 'checkbox' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :options="field.options || []"
        :index="i"
        :width="field.width || '100%'"
        @return-values="updateForm"
      />
      <CheckboxField
        v-if="field.type === 'single-checkbox' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :index="i"
        :value="field.value"
        :width="field.width || '100%'"
        @return-value="updateForm({ ...field, index: i, value: $event })"
      />
      <RadioInput
        v-if="field.type === 'radio' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :custom="field.custom"
        :subtitle="field.subtitle"
        :options="field.options || []"
        :index="i"
        :value="field.value"
        :width="field.width || '100%'"
        @return-value="updateForm({ ...field, index: i, value: $event })"
      />
      <DropDown
        v-if="field.type === 'dropdown' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :options="field.options || []"
        :searchable="field.searchable"
        :clearable="field.clearable === true"
        :width="field.width || '100%'"
        :value="field.value as any"
        :index="i"
        @return-value="updateForm({ ...field, index: i, value: $event } as any)"
        @return-search="updateSearchTerm"
      />
      <DateInput
        v-if="field.type === 'date' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :yearly="true"
        :index="i"
        @return-value="updateForm"
      />
      <PhoneInput
        v-if="field.type === 'phone' && field.show"
        :id="field.id"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :is-required="field.required"
        :error-message="field.error"
        :show-title="field.showTitle"
        :index="i"
        :value="field.value"
        :width="field.width || '99%'"
        autocomplete="tel"
        @return-value="updateForm({ ...field, ...$event })"
      />
      <TextInput
        v-else-if="
          field.type !== 'dropdown' &&
          field.type !== 'radio' &&
          field.type !== 'checkbox' &&
          field.type !== 'date' &&
          field.type !== 'single-checkbox' &&
          field.show
        "
        :id="field.id"
        :ref="(el) => setTextInputRef(el, i)"
        class="dynamic-form__wrapper--field"
        :title="field.title"
        :subtitle="field.subtitle"
        :type="field.type"
        :value="field.value"
        :place-holder="field.placeHolder || field.title"
        :name="field.name"
        :is-required="field.required"
        :error-message="field.error"
        :is-uppercase="field.uppercase"
        :show-title="field.showTitle"
        :index="i"
        :width="field.width || '100%'"
        :stacked="stacked"
        :autocomplete="field.name"
        :disabled="field.disabled"
        :login="field.login"
        :rows="field.type === 'textarea' ? 5 : 1"
        :max-length="field.type === 'textarea' ? 1000 : undefined"
        @return-value="updateForm"
        @enter-pressed="enterPressed"
      />
    </div>
  </div>
</template>

<style lang="scss">
.dynamic-form {
  width: 100%;
  position: relative;
  &__wrapper {
    &--field {
      margin: 1rem 0;
      @media (max-width: $tablet) {
        margin: 1rem 0;
      }
    }
  }
}
</style>
